/**
 * Push notification design defaults.
 *
 * Each entry is a complete PushBuilderData ready to drop into the Push
 * builder. Operators pick one as a starting point and edit freely.
 */

import {
  PushBuilderData,
  PushClickBehavior,
  PushPlatforms,
} from "pages/PushBuilder/PushBuilderContent";

export interface PushDefault {
  key: string;
  category: string;
  name: string;
  description: string;
  data: PushBuilderData;
}

const baseSettings = (
  overrides: Partial<{
    title: string;
    description: string;
    summary: string;
    withImage: boolean;
    clickType: PushClickBehavior;
    webURL: string;
  }> = {}
) => ({
  title: overrides.title ?? "",
  description: overrides.description ?? "",
  image: overrides.withImage ? { key: "", imageSrc: "" } : undefined,
  clickBehavior: {
    type: overrides.clickType ?? PushClickBehavior.OPEN_APP,
    webURL: overrides.webURL ?? "",
  },
  summary: overrides.summary ?? "",
  expandedImage: overrides.withImage ? { key: "", imageSrc: "" } : undefined,
});

const buildData = (
  settings: ReturnType<typeof baseSettings>
): PushBuilderData => ({
  platform: {
    [PushPlatforms.ANDROID]: true,
    [PushPlatforms.IOS]: true,
  },
  keepContentConsistent: true,
  settings: {
    [PushPlatforms.ANDROID]: settings,
    [PushPlatforms.IOS]: settings,
  },
  fields: [],
});

// ---------- 1. Plain Alert ----------
const plainAlert: PushDefault = {
  key: "push.plain-alert",
  category: "Notification",
  name: "Plain Alert",
  description:
    "Title and one-line description. No image. Opens the app on tap.",
  data: buildData(
    baseSettings({
      title: "Heads up",
      description: "A short, useful line of context.",
      clickType: PushClickBehavior.OPEN_APP,
    })
  ),
};

// ---------- 2. Image Announcement ----------
const imageAnnouncement: PushDefault = {
  key: "push.image-announcement",
  category: "Notification",
  name: "Image Announcement",
  description:
    "Title, description, and a feature image. For launches and rich updates.",
  data: buildData(
    baseSettings({
      title: "Something new",
      description: "One line about what's new and why it matters.",
      withImage: true,
      clickType: PushClickBehavior.OPEN_APP,
    })
  ),
};

// ---------- 3. Web Link CTA ----------
const webLinkCta: PushDefault = {
  key: "push.web-link",
  category: "Action",
  name: "Web Link CTA",
  description:
    "Title and description that opens a URL instead of the app on tap.",
  data: buildData(
    baseSettings({
      title: "Have a look at this",
      description: "Tap to open the page.",
      clickType: PushClickBehavior.REDIRECT_URL,
      webURL: "https://",
    })
  ),
};

// ---------- 4. Rich Expanded Update ----------
const richExpanded: PushDefault = {
  key: "push.rich-expanded",
  category: "Notification",
  name: "Rich Expanded Update",
  description:
    "Title, short description, and a longer summary that shows when the notification is expanded (Android).",
  data: buildData(
    baseSettings({
      title: "An update worth a few seconds",
      description: "Short preview line.",
      summary:
        "Longer text that appears when the recipient pulls down to expand the notification. Use this when one line isn't enough.",
      clickType: PushClickBehavior.OPEN_APP,
    })
  ),
};

export const PUSH_DEFAULTS: PushDefault[] = [
  plainAlert,
  imageAnnouncement,
  webLinkCta,
  richExpanded,
];
